/**
 * Quick License Plate Calculator - Vehicle Classification
 *
 * Heuristic-based classification to determine whether a vehicle
 * is likely a MARGE (margin scheme) or BTW (VAT) vehicle.
 *
 * IMPORTANT: This is an ADVISORY classification only.
 * The actual VAT/margin status depends on the vehicle's history,
 * previous owners, and import documentation.
 */

import type { RdwDraft } from '@/lib/rdwClient';
import type {
  ClassificationResult,
  VatMarginClassification,
  ClassificationConfidence,
} from './types';

/**
 * Classification scoring weights
 */
interface ClassificationScore {
  marginPoints: number;
  vatPoints: number;
  reasons: string[];
}

/**
 * Calculate months between two dates
 */
function monthsBetween(startDate: Date, endDate: Date): number {
  const months =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth());
  return Math.max(0, months);
}

/**
 * Parse ISO date string to Date object
 */
function parseDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Check if fuel type indicates electric vehicle
 */
function isElectricFuel(fuelType: string | null | undefined): boolean {
  if (!fuelType) return false;
  const normalized = fuelType.toLowerCase();
  return normalized.includes('elektr') || normalized === 'electric';
}

/**
 * Classify a vehicle as likely MARGE or BTW based on RDW data
 *
 * Scoring system:
 * - Import indicators → +2 points toward BTW (imports usually BTW)
 * - Age > 6 months → +1 point toward MARGE
 * - Age > 3 years → +2 points toward MARGE
 * - brutoBpm = 0 or missing → +1 point toward MARGE
 * - firstRegDateNL = firstRegDate → +1 point toward MARGE (NL origin)
 * - Age < 6 months → +2 points toward BTW (likely new)
 *
 * @param rdwData - Vehicle data from RDW lookup
 * @returns Classification result with confidence and reasons
 */
export function classifyVehicle(rdwData: RdwDraft): ClassificationResult {
  const score: ClassificationScore = {
    marginPoints: 0,
    vatPoints: 0,
    reasons: [],
  };

  const today = new Date();
  const firstRegDate = parseDate(rdwData.firstRegDate);
  const firstRegDateNL = parseDate(rdwData.firstRegDateNL);

  // Calculate age in months
  let ageMonths: number | null = null;
  if (firstRegDate) {
    ageMonths = monthsBetween(firstRegDate, today);
  }

  // === IMPORT DETECTION ===
  // If firstRegDateNL differs from firstRegDate, it's an import
  if (firstRegDate && firstRegDateNL) {
    const isImport = firstRegDate.getTime() !== firstRegDateNL.getTime();

    if (isImport) {
      // Imports are typically BTW vehicles
      score.vatPoints += 2;
      score.reasons.push('Geïmporteerd voertuig (BTW waarschijnlijk)');
    } else {
      // NL origin vehicles are more likely MARGE
      score.marginPoints += 1;
      score.reasons.push('Nederlandse herkomst');
    }
  }

  // === AGE-BASED SCORING ===
  if (ageMonths !== null) {
    if (ageMonths < 6) {
      // Very new vehicle → likely BTW
      score.vatPoints += 2;
      score.reasons.push(`Jonge auto (${ageMonths} maanden) - waarschijnlijk BTW`);
    } else if (ageMonths >= 6 && ageMonths < 36) {
      // 6 months to 3 years → slight MARGE tendency
      score.marginPoints += 1;
      score.reasons.push(`Leeftijd ${ageMonths} maanden`);
    } else if (ageMonths >= 36) {
      // Over 3 years → strong MARGE tendency
      score.marginPoints += 2;
      score.reasons.push(`Auto ouder dan 3 jaar (${Math.floor(ageMonths / 12)} jaar)`);
    }
  } else {
    score.reasons.push('Eerste registratiedatum onbekend');
  }

  // === BPM-BASED SCORING ===
  if (rdwData.brutoBpm === 0 || rdwData.brutoBpm === null) {
    // No BPM or zero BPM → more likely MARGE
    score.marginPoints += 1;
    if (rdwData.brutoBpm === 0) {
      score.reasons.push('Bruto BPM is €0');
    } else {
      score.reasons.push('Bruto BPM onbekend');
    }
  }

  // === ELECTRIC VEHICLE ===
  if (isElectricFuel(rdwData.fuelType)) {
    // Electric vehicles have special BPM rules (usually 0)
    // This doesn't strongly indicate MARGE or BTW
    score.reasons.push('Elektrisch voertuig (BPM = €0)');
  }

  // === DETERMINE CLASSIFICATION ===
  const netScore = score.marginPoints - score.vatPoints;
  let classification: VatMarginClassification;
  let confidence: ClassificationConfidence;

  if (netScore >= 3) {
    classification = 'LIKELY_MARGIN';
    confidence = 'HIGH';
  } else if (netScore >= 1) {
    classification = 'LIKELY_MARGIN';
    confidence = 'MEDIUM';
  } else if (netScore <= -3) {
    classification = 'LIKELY_VAT';
    confidence = 'HIGH';
  } else if (netScore <= -1) {
    classification = 'LIKELY_VAT';
    confidence = 'MEDIUM';
  } else {
    classification = 'UNCERTAIN';
    confidence = 'LOW';
  }

  // === ADD DISCLAIMER ===
  score.reasons.push(
    '⚠️ Dit is een indicatie. De werkelijke BTW/marge-status hangt af van de herkomst en eerdere eigenaren.'
  );

  return {
    classification,
    confidence,
    reasons: score.reasons,
  };
}

/**
 * Get human-readable classification label (Dutch)
 */
export function getClassificationLabel(classification: VatMarginClassification): string {
  switch (classification) {
    case 'LIKELY_MARGIN':
      return 'Waarschijnlijk MARGE';
    case 'LIKELY_VAT':
      return 'Waarschijnlijk BTW';
    case 'UNCERTAIN':
      return 'Onduidelijk';
  }
}

/**
 * Get human-readable confidence label (Dutch)
 */
export function getConfidenceLabel(confidence: ClassificationConfidence): string {
  switch (confidence) {
    case 'HIGH':
      return 'Hoog';
    case 'MEDIUM':
      return 'Gemiddeld';
    case 'LOW':
      return 'Laag';
  }
}
